// Structured logging for a broker-consumer worker — `@bindery/logging`'s one
// Worker logger (`createWorkerLogger`), bound to the app's `service` and
// wrapped in the two method names every consumer already calls.
//
// Rules honored at the call sites: `domain.action` event names in the `auth`
// domain (§2, enforced by the shared logger); one rich event per operation
// (§7); payload keys inside `data` (§12); and NEVER a JWT, email, or any PII
// (§4) — only opaque reasons/counters/status codes.
//
// The emitted shape is the shared Worker line, `{service, eventName, level,
// data, error?}`, and the apps share one Axiom dataset: `service` is the
// discriminator operators lead every query with (`docs/ops/logging.md`), which
// is how retells' worker once silently dropped its own events by omitting
// both fields. `service` is bound from `ConsumerAppConfig.service`.
import type { EventName, LogError } from "@bindery/logging/types";
import {
  createWorkerLogger,
  type WorkerLogLevel,
} from "@bindery/logging/worker-logger";

export type LogLevel = WorkerLogLevel;

export interface AuthLogData {
  /** Distinguishing failure case, e.g. `exchange:status_403`, `verify:signature`. */
  reason?: string;
  /** Circuit-breaker attempt count (guardrail 7). */
  tries?: number;
  host?: string;
  duration_ms?: number;
  [key: string]: unknown;
}

export interface AuthLogger {
  /**
   * Emit a single structured worker event onto the native Cloudflare log
   * stream. `eventName` MUST be `domain.action` (§2) — anything else throws;
   * `data` carries the case-distinguishing detail (§7/§12) and must never
   * hold a JWT, email, or any PII (§4). `error`, when there is one, is the
   * closed three-key shape and lands at the top level, where every other
   * tier's errors are queried (§10).
   */
  logWorkerEvent: (
    eventName: EventName,
    data?: AuthLogData,
    level?: LogLevel,
    error?: LogError,
  ) => void;
  /**
   * Emit a single structured auth event. `eventName` MUST be `auth.<action>`;
   * callers log BEFORE any early return/redirect (guardrail 3) so no failure
   * is silent.
   */
  logAuthEvent: (
    eventName: `auth.${Lowercase<string>}`,
    data?: AuthLogData,
    level?: LogLevel,
  ) => void;
}

/** Bind the emitter to one app's `service` discriminator. */
export function createLogger(service: string): AuthLogger {
  const logger = createWorkerLogger<AuthLogData>(service);
  const logWorkerEvent = (
    eventName: EventName,
    data: AuthLogData = {},
    level?: LogLevel,
    error?: LogError,
  ): void => {
    // An unset level is the shared default: "error" with an error, else "info".
    logger.write(eventName, data, error, level === undefined ? {} : { level });
  };
  return {
    logWorkerEvent,
    logAuthEvent: (eventName, data, level) =>
      logWorkerEvent(eventName, data, level),
  };
}
